import { Flex, Skeleton, TableProps } from '@chakra-ui/react';
import { ReactElement, useMemo } from 'react';
import { message } from '../../../../utils';
import { Icon, IconName } from '../../atoms';
import { Message } from '../message/message';
import { Pagination } from '../pagination';
import { PFTable } from '../payflip-table/payflip-table';
import { ColumnDefinitionProps } from './column-definition';

type SortingProps<T> = {
  columnName: keyof T;
  direction: 'asc' | 'desc';
};

type Data<T> = Array<{ [K in keyof T]: T[K] }>;

type PaginatedTableProps<T extends object> = {
  /**
   * Default pagination the component will be initialised with.
   * @param pagination.page The default page that will be shown
   * @param pagination.totalPages The total amount of pages
   */
  pagination?: {
    page: number;
    totalPages: number;
  };
  /**
   * Default sorting on the table. This property accepts a property key
   * and the corresponding ascending or descending sorting algorithm.
   */
  sorting?: SortingProps<Partial<T>>;
  /**
   * Data fetching callback. This callback is called whenever a change is made
   * in the sorting or pagination of the table. The data should be fetched
   * higher up in the application and fed back into the component via the `data` property.
   * Please also note that it is **your** responsibility to set the state of your master component
   * to save on which page you are.
   */
  onChange?: (
    sorting: SortingProps<Partial<T>> | undefined,
    page: number
  ) => void;
  hasNoBorderDesign?: boolean;
  data: Data<T>;
  /**
   * Optional callback that is to be executed when clicking the row
   */
  onClickRow?: (row: {
    [K in keyof T]: T[K];
  }) => void;
  children?: Array<ReactElement<ColumnDefinitionProps<T>>>;
  selectedRow?: number;

  isLoading?: boolean;
  amountOfLoadingRows?: number;
  emptyMessage?: string;
  showErrorMessage?: boolean;
  errorMessage?: string;
};

/**
 * Table component that can handle paginated and sorted content.
 * Even though this component may seem smart, it is still dumb since it relies
 * on the backend to fetch the appropriate data based on sorting and pagination
 * props. This is to offload the frontend and server from fetching very large lists.
 *
 * Headers are defined using the ColumnDefinition component and associated props. To render custom cells,
 * the `render` property on the ColumnDefinition can be used to return a custom `ReactNode`.
 */
function PaginatedTable<T extends object>({
  sorting,
  data,
  onChange,
  pagination = {
    page: 1,
    totalPages: 1
  },
  children,
  onClickRow,
  selectedRow,
  isLoading = false,
  amountOfLoadingRows = 5,
  emptyMessage,
  showErrorMessage = false,
  errorMessage,
  ...props
}: Omit<TableProps, 'onChange'> & PaginatedTableProps<T>): JSX.Element {
  // Columns are constructed based on the children props (if any)
  const columns: Array<ColumnDefinitionProps<T>> = (children ?? [])
    .map((el: ReactElement<ColumnDefinitionProps<T>>) => el.props)
    .sort((a, b) => {
      if ((a.order ?? 0) > (b.order ?? 0)) {
        return 1;
      } else if ((a.order ?? 0) < (b.order ?? 0)) {
        return -1;
      } else {
        return 0;
      }
    });

  // Set default sorting whenever our data comes in, optional
  const sort = (columnName: keyof T): void => {
    const isCurrentColumn = sorting?.columnName === columnName;
    const direction = 'asc';

    if (!isCurrentColumn) {
      onChange?.(
        {
          columnName,
          direction
        },
        pagination?.page ?? 1
      );
    } else if (isCurrentColumn && sorting?.direction === 'asc') {
      onChange?.(
        {
          columnName,
          direction: 'desc'
        },
        pagination?.page ?? 1
      );
    } else {
      onChange?.(undefined, pagination?.page ?? 1);
    }
  };

  const tableData = data ?? [];

  const skeletonRows = useMemo(
    () => Array.from({ length: amountOfLoadingRows }),
    [amountOfLoadingRows]
  );

  const showSkeletonRows = isLoading && !errorMessage && tableData.length === 0;

  return (
    <>
      <PFTable style={{ tableLayout: 'fixed' }} {...props}>
        <PFTable.Header>
          {columns?.map((headerProps, headerIndex) => (
            <PFTable.HeaderCell
              key={`${headerIndex}-${headerProps.property as string}`}
              style={{ paddingLeft: 0 }}
              w={headerProps.width ?? 'auto'}
            >
              <Flex
                justifyContent="flex-start"
                alignItems="center"
                ml={'space_unit.x2'}
                onClick={(): boolean | void =>
                  headerProps.sortable !== false && sort(headerProps.property)
                }
              >
                {headerProps.sortable !== false && (
                  <Flex
                    h="0.8rem"
                    flexDir="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ svg: { maxH: '50%' } }}
                  >
                    <Icon
                      name={IconName.ArrowUp}
                      color={
                        sorting?.columnName === headerProps.property &&
                        sorting?.direction === 'asc'
                          ? '#000'
                          : 'currentColor'
                      }
                      visibility={
                        sorting?.columnName !== headerProps.property ||
                        (sorting?.columnName === headerProps.property &&
                          sorting?.direction === 'asc')
                          ? 'visible'
                          : 'hidden'
                      }
                    />
                    <Icon
                      name={IconName.ArrowDown}
                      color={
                        sorting?.columnName === headerProps.property &&
                        sorting?.direction === 'desc'
                          ? '#000'
                          : 'currentColor'
                      }
                      visibility={
                        sorting?.columnName !== headerProps.property ||
                        (sorting?.columnName === headerProps.property &&
                          sorting?.direction === 'desc')
                          ? 'visible'
                          : 'hidden'
                      }
                    />
                  </Flex>
                )}
                {typeof headerProps.label === 'function'
                  ? headerProps.label()
                  : headerProps.label}
              </Flex>
            </PFTable.HeaderCell>
          ))}
        </PFTable.Header>
        {showErrorMessage && (
          <PFTable.Row>
            <PFTable.Cell colSpan={columns.length}>
              <Flex justifyContent="center">
                <Message
                  text={errorMessage}
                  fallback={message(null, `Coudn't load data properly.`)}
                />
              </Flex>
            </PFTable.Cell>
          </PFTable.Row>
        )}
        {tableData?.length === 0 && !showErrorMessage && emptyMessage && (
          <PFTable.Row>
            <PFTable.Cell colSpan={columns.length}>
              <Flex justifyContent="center">
                <Message
                  text={emptyMessage}
                  fallback={message(null, `No data available.`)}
                />
              </Flex>
            </PFTable.Cell>
          </PFTable.Row>
        )}
        {showSkeletonRows &&
          skeletonRows.map((_, index) => (
            <PFTable.Row key={index}>
              {columns.map((_, colIndex) => (
                <PFTable.Cell key={`${index}-${colIndex}`}>
                  <Skeleton h="24px" w="full" />
                </PFTable.Cell>
              ))}
            </PFTable.Row>
          ))}
        {!showErrorMessage &&
          tableData?.map((row, j) => (
            <PFTable.Row
              key={j}
              clickable={typeof onClickRow === 'function'}
              onClick={(): void => onClickRow?.(row)}
              backgroundColor={selectedRow === j ? 'palette_blue.0' : undefined}
            >
              {columns.map((header, k) => (
                <PFTable.Cell
                  maxW="20rem"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  key={`${j}-${k}`}
                >
                  {typeof header.render === 'function' ? (
                    header.render(row)
                  ) : (
                    <span title={row[header.property]?.toString()}>
                      {row[header.property]?.toString()}
                    </span>
                  )}
                </PFTable.Cell>
              ))}
            </PFTable.Row>
          ))}
      </PFTable>
      {!showErrorMessage && pagination?.totalPages > 1 && (
        <Flex p={4}>
          <Pagination
            pageCount={pagination?.totalPages}
            currentPage={pagination?.page ?? 1}
            onPage={(pageNumber): void => onChange?.(sorting, pageNumber)}
          />
        </Flex>
      )}
    </>
  );
}

export { PaginatedTable, Data as PaginatedTableData, SortingProps };
